import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { annualBonusAPI, organizationAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Users, Gift, CheckCircle2, CreditCard, Download, Search,
  CheckCheck, RotateCcw, Filter, ArrowUpDown, IndianRupee, Loader2, UserX, CalendarRange,
} from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function defaultFY() {
  const now = new Date();
  const y = now.getFullYear();
  if (now.getMonth() + 1 >= 4) return { sm: 4, sy: y, em: 3, ey: y + 1 };
  return { sm: 4, sy: y - 1, em: 3, ey: y };
}

function KPICard({ icon, label, value, sub, borderColor }) {
  return (
    <Card className="border-l-4" style={{ borderLeftColor: borderColor }}>
      <CardContent className="py-4 px-5 flex items-center gap-4">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted/60">
          {icon}
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">{label}</p>
          <p className="text-xl font-bold tracking-tight">{value}</p>
          {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function ProRateBadge({ months, periodLen }) {
  if (months >= periodLen) return null;
  return (
    <span className="ml-1.5 px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-amber-100 text-amber-700 border border-amber-200">
      Pro-rated
    </span>
  );
}

function StatusPill({ isFinalized, isPaid }) {
  if (isPaid) return <span className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">Paid</span>;
  if (isFinalized) return <span className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-blue-100 text-blue-700 border border-blue-200">Finalized</span>;
  return <span className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-gray-100 text-gray-500 border border-gray-200">Pending</span>;
}

function EmpStatusBadge({ status, lwd }) {
  if (status === 'on_notice') {
    return <span className="inline-flex px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-amber-100 text-amber-700 border border-amber-200">Resigned{lwd ? ` · LWD ${lwd}` : ''}</span>;
  }
  if (status && status !== 'active') {
    return <span className="inline-flex px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-red-100 text-red-700 border border-red-200">Exited{lwd ? ` · LWD ${lwd}` : ''}</span>;
  }
  return null;
}

function PayoutRow({ row, index, periodLen, isSuperAdmin, isAdmin, onFinalize, onPay, onRevert, onUpdateAmount, onApplyProrate }) {
  const [amount, setAmount] = useState(row.total_payout);
  const [saving, setSaving] = useState(false);
  const isEditable = !row.is_finalized && (isSuperAdmin || isAdmin);
  const isFlagged = row.employee_status && row.employee_status !== 'active';
  const hasSuggestion = isEditable && row.suggested_months !== null && row.suggested_months !== undefined;

  useEffect(() => { setAmount(row.total_payout); }, [row.total_payout]);

  const handleBlur = async () => {
    if (amount !== row.total_payout) {
      setSaving(true);
      await onUpdateAmount(row.id, amount);
      setSaving(false);
    }
  };

  return (
    <tr className={`border-b transition-colors text-sm ${isFlagged ? 'bg-amber-50/40 dark:bg-amber-950/10' : row.is_paid ? 'bg-emerald-50/30 dark:bg-emerald-950/10' : row.is_finalized ? 'bg-blue-50/20 dark:bg-blue-950/10' : 'hover:bg-muted/30'}`} data-testid={`bonus-row-${row.emp_code}`}>
      <td className="py-3 px-4 text-muted-foreground text-center text-xs">{index + 1}</td>
      <td className="py-3 px-4 font-mono text-xs font-medium">{row.emp_code}</td>
      <td className="py-3 px-4">
        <div className="font-medium text-sm">{row.name}</div>
        <EmpStatusBadge status={row.employee_status} lwd={row.last_working_date} />
      </td>
      <td className="py-3 px-4 text-xs text-muted-foreground">{row.organization}</td>
      <td className="py-3 px-4 text-xs text-muted-foreground">{row.joining_date || '—'}</td>
      <td className="py-3 px-4 text-right">
        <span className="font-mono text-xs">{fmt(row.annual_bonus)}</span>
        <div className="text-[10px] text-muted-foreground">₹{fmt(row.monthly_bonus)}/mo</div>
      </td>
      <td className="py-3 px-4 text-center">
        <span className="font-semibold text-sm">{row.eligible_months}</span>
        <ProRateBadge months={row.eligible_months} periodLen={periodLen} />
        {hasSuggestion && (
          <div className="mt-1">
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[10px] text-amber-700 border-amber-300 hover:bg-amber-50"
              onClick={() => onApplyProrate(row.id)}
              data-testid={`bonus-apply-prorate-${row.emp_code}`}
            >
              Pro-rate → {row.suggested_months} mo (₹{fmt(row.suggested_payout)})
            </Button>
          </div>
        )}
      </td>
      <td className="py-3 px-4 text-right">
        {isEditable ? (
          <div className="relative w-[130px] ml-auto">
            <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="number"
              className="pl-7 text-right font-mono text-sm h-8"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              onBlur={handleBlur}
              disabled={saving}
              data-testid={`bonus-amount-${row.emp_code}`}
            />
          </div>
        ) : (
          <span className="font-mono font-bold text-sm text-emerald-700">{fmt(row.total_payout)}</span>
        )}
      </td>
      <td className="py-3 px-4 text-center"><StatusPill isFinalized={row.is_finalized} isPaid={row.is_paid} /></td>
      <td className="py-3 px-4 text-center">
        <Checkbox
          checked={row.is_finalized}
          disabled={row.is_finalized}
          onCheckedChange={() => onFinalize(row.id)}
          data-testid={`bonus-finalize-cb-${row.emp_code}`}
        />
      </td>
      <td className="py-3 px-4 text-center">
        <Checkbox
          checked={row.is_paid}
          disabled={!row.is_finalized || row.is_paid}
          onCheckedChange={() => onPay(row.id)}
          data-testid={`bonus-paid-cb-${row.emp_code}`}
        />
      </td>
      <td className="py-3 px-4 text-center">
        {isSuperAdmin && (row.is_finalized || row.is_paid) ? (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => onRevert(row.id)} data-testid={`bonus-revert-btn-${row.emp_code}`}>
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

function exportCSV(records) {
  const headers = ['Emp ID','Employee','Emp Status','Last Working Date','Organization','Joining Date','Annual Bonus','Monthly Bonus','Months','Total Payout','Status','Finalized','Paid At'];
  const rows = [];
  for (let i = 0; i < records.length; i++) {
    const r = records[i];
    const st = r.is_paid ? 'Paid' : r.is_finalized ? 'Finalized' : 'Pending';
    const es = r.employee_status === 'on_notice' ? 'Resigned' : r.employee_status === 'active' ? 'Active' : 'Exited';
    rows.push([r.emp_code,r.name,es,r.last_working_date || '',r.organization,r.joining_date,r.annual_bonus,r.monthly_bonus,r.eligible_months,r.total_payout,st,r.finalized_at,r.paid_at].join(','));
  }
  const csv = headers.join(',') + '\n' + rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'annual_bonus_payout.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function monthYearOptions(items) {
  const els = [];
  for (let i = 0; i < items.length; i++) {
    els.push(<SelectItem key={String(items[i].v)} value={String(items[i].v)}>{items[i].l}</SelectItem>);
  }
  return els;
}

export default function AnnualBonusPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin;

  const fy = defaultFY();
  const [startMonth, setStartMonth] = useState(String(fy.sm));
  const [startYear, setStartYear] = useState(String(fy.sy));
  const [endMonth, setEndMonth] = useState(String(fy.em));
  const [endYear, setEndYear] = useState(String(fy.ey));
  const [filterOrg, setFilterOrg] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('empId');
  const [records, setRecords] = useState([]);
  const [periodLen, setPeriodLen] = useState(12);
  const [totals, setTotals] = useState({ count: 0, total_payout: 0, finalized: 0, paid: 0, pending_amount: 0 });
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const period = `${MONTH_KEYS[parseInt(startMonth) - 1]}${startYear}-${MONTH_KEYS[parseInt(endMonth) - 1]}${endYear}`;
  const spanMonths = (parseInt(endYear) * 12 + parseInt(endMonth)) - (parseInt(startYear) * 12 + parseInt(startMonth)) + 1;
  const periodValid = spanMonths >= 1 && spanMonths <= 12;

  const loadData = useCallback(async () => {
    if (!periodValid) { setRecords([]); return; }
    setLoading(true);
    try {
      const params = {};
      if (filterOrg !== 'all') params.organization_id = filterOrg;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (search.trim()) params.search = search.trim();
      const res = await annualBonusAPI.getByPeriod(period, params);
      setRecords(res.data?.records || []);
      setPeriodLen(res.data?.period_len || 12);
      setTotals(res.data?.totals || { count: 0, total_payout: 0, finalized: 0, paid: 0, pending_amount: 0 });
    } catch {
      toast.error('Failed to load annual bonus payouts');
    } finally {
      setLoading(false);
    }
  }, [period, periodValid, filterOrg, filterStatus, search]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    organizationAPI.getAll().then(r => setOrgs(r.data || [])).catch(() => {});
  }, []);

  const handleGenerate = async () => {
    if (!periodValid) {
      toast.error(spanMonths < 1 ? 'Period end is before start' : 'Period cannot exceed 12 months');
      return;
    }
    setGenerating(true);
    try {
      const orgParam = filterOrg !== 'all' ? filterOrg : undefined;
      const res = await annualBonusAPI.generate(period, orgParam);
      toast.success(res.data?.message || 'Payouts generated');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate');
    } finally {
      setGenerating(false);
    }
  };

  const handleFinalize = async (id) => {
    try {
      await annualBonusAPI.finalize(id);
      toast.success('Finalized');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handlePay = async (id) => {
    try {
      await annualBonusAPI.markPaid(id);
      toast.success('Marked as paid');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRevert = async (id) => {
    try {
      await annualBonusAPI.revert(id);
      toast.success('Reverted');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Only Super Admin can revert'); }
  };

  const handleUpdateAmount = async (id, amount) => {
    try {
      await annualBonusAPI.updateAmount(id, amount);
      toast.success('Amount updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const handleApplyProrate = async (id) => {
    try {
      const res = await annualBonusAPI.applyProrate(id);
      toast.success(`Pro-rated: ${res.data.eligible_months} months`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to pro-rate'); }
  };

  const handleBulkProrate = async () => {
    try {
      const res = await annualBonusAPI.bulkProrate(period);
      toast.success(res.data?.message || 'Pro-rated');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleBulkFinalize = async () => {
    try {
      const orgParam = filterOrg !== 'all' ? filterOrg : undefined;
      const res = await annualBonusAPI.bulkFinalize(period, orgParam);
      toast.success(res.data?.message || 'All finalized');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleBulkPay = async () => {
    try {
      const orgParam = filterOrg !== 'all' ? filterOrg : undefined;
      const res = await annualBonusAPI.bulkPay(period, orgParam);
      toast.success(res.data?.message || 'All marked paid');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleBulkRevert = async () => {
    try {
      const orgParam = filterOrg !== 'all' ? filterOrg : undefined;
      const res = await annualBonusAPI.bulkRevert(period, orgParam);
      toast.success(res.data?.message || 'All reverted');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Only Super Admin can revert'); }
  };

  const sorted = [...records];
  if (sortBy === 'name') sorted.sort((a, b) => a.name.localeCompare(b.name));
  else if (sortBy === 'amount-desc') sorted.sort((a, b) => b.total_payout - a.total_payout);
  else if (sortBy === 'amount-asc') sorted.sort((a, b) => a.total_payout - b.total_payout);
  else if (sortBy === 'status') sorted.sort((a, b) => (a.is_paid ? 2 : a.is_finalized ? 1 : 0) - (b.is_paid ? 2 : b.is_finalized ? 1 : 0));

  const tableRows = [];
  for (let i = 0; i < sorted.length; i++) {
    tableRows.push(
      <PayoutRow
        key={sorted[i].id}
        row={sorted[i]}
        index={i}
        periodLen={periodLen}
        isSuperAdmin={isSuperAdmin}
        isAdmin={isAdmin}
        onFinalize={handleFinalize}
        onPay={handlePay}
        onRevert={handleRevert}
        onUpdateAmount={handleUpdateAmount}
        onApplyProrate={handleApplyProrate}
      />
    );
  }

  const orgOptions = [];
  for (let i = 0; i < orgs.length; i++) {
    orgOptions.push(<SelectItem key={orgs[i].id} value={orgs[i].id}>{orgs[i].name}</SelectItem>);
  }

  const monthOpts = [];
  for (let i = 0; i < 12; i++) monthOpts.push({ v: i + 1, l: MONTH_LABELS[i] });
  const yearOpts = [];
  const thisYear = new Date().getFullYear();
  for (let y = thisYear + 1; y >= 2022; y--) yearOpts.push({ v: y, l: String(y) });

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="annual-bonus-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 tracking-tight">
              <Gift className="h-6 w-6 text-violet-500" />
              Annual Bonus Payout
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Annual bonus disbursement — pro-rated by months worked in the cycle</p>
          </div>
          {isAdmin && (
            <Button className="gap-2 bg-violet-600 hover:bg-violet-700 shadow-sm" onClick={handleGenerate} disabled={generating} data-testid="bonus-generate-btn">
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {generating ? 'Generating...' : 'Generate Payouts'}
            </Button>
          )}
        </div>

        {/* Period Bar */}
        <Card className="bg-muted/20">
          <CardContent className="py-3 px-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <CalendarRange className="h-3.5 w-3.5" />
                Bonus Cycle
              </div>
              <Select value={startMonth} onValueChange={setStartMonth}>
                <SelectTrigger className="w-[90px] bg-background h-9" data-testid="bonus-start-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>{monthYearOptions(monthOpts)}</SelectContent>
              </Select>
              <Select value={startYear} onValueChange={setStartYear}>
                <SelectTrigger className="w-[90px] bg-background h-9" data-testid="bonus-start-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>{monthYearOptions(yearOpts)}</SelectContent>
              </Select>
              <span className="text-muted-foreground text-sm">to</span>
              <Select value={endMonth} onValueChange={setEndMonth}>
                <SelectTrigger className="w-[90px] bg-background h-9" data-testid="bonus-end-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>{monthYearOptions(monthOpts)}</SelectContent>
              </Select>
              <Select value={endYear} onValueChange={setEndYear}>
                <SelectTrigger className="w-[90px] bg-background h-9" data-testid="bonus-end-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>{monthYearOptions(yearOpts)}</SelectContent>
              </Select>
              {!periodValid && (
                <span className="text-xs text-red-600 font-medium" data-testid="period-invalid-msg">
                  {spanMonths < 1 ? 'End is before start' : 'Max 12 months'}
                </span>
              )}
              {periodValid && (
                <span className="text-xs text-muted-foreground">{spanMonths} month cycle</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Filters Bar */}
        <Card className="bg-muted/20">
          <CardContent className="py-3 px-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <Filter className="h-3.5 w-3.5" />
                Filters
              </div>
              <Select value={filterOrg} onValueChange={setFilterOrg}>
                <SelectTrigger className="w-[210px] bg-background h-9" data-testid="bonus-org-select">
                  <SelectValue placeholder="All Organizations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Organizations</SelectItem>
                  {orgOptions}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px] bg-background h-9" data-testid="bonus-status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="finalized">Finalized</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative flex-1 min-w-[180px] max-w-[260px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search by name or ID..."
                  className="pl-8 bg-background h-9 text-sm"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="bonus-search"
                />
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[150px] bg-background h-9" data-testid="bonus-sort-select">
                  <div className="flex items-center gap-1.5">
                    <ArrowUpDown className="h-3 w-3" />
                    <SelectValue placeholder="Sort by" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="empId">Emp ID</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="amount-desc">Amount (High-Low)</SelectItem>
                  <SelectItem value="amount-asc">Amount (Low-High)</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <KPICard
            icon={<Users className="h-5 w-5 text-blue-600" />}
            label="Eligible Employees"
            value={totals.count}
            sub="with annual bonus > 0"
            borderColor="#3b82f6"
          />
          <KPICard
            icon={<UserX className="h-5 w-5 text-amber-600" />}
            label="Resigned / Exited"
            value={totals.resigned_count || 0}
            sub="flagged — review pro-rate"
            borderColor="#f59e0b"
          />
          <KPICard
            icon={<Gift className="h-5 w-5 text-emerald-600" />}
            label="Total Payout"
            value={fmt(totals.total_payout)}
            sub={`Pending: ${fmt(totals.pending_amount)}`}
            borderColor="#10b981"
          />
          <KPICard
            icon={<CheckCircle2 className="h-5 w-5 text-orange-600" />}
            label="Finalized"
            value={`${totals.finalized} / ${totals.count}`}
            sub={`${totals.count - totals.finalized} remaining`}
            borderColor="#f97316"
          />
          <KPICard
            icon={<CreditCard className="h-5 w-5 text-violet-600" />}
            label="Paid"
            value={`${totals.paid} / ${totals.count}`}
            sub={`${totals.finalized - totals.paid} awaiting payment`}
            borderColor="#8b5cf6"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
          </div>
        ) : records.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Gift className="h-14 w-14 mb-3 opacity-30" />
              <p className="text-lg font-medium">No annual bonus payouts found</p>
              <p className="text-sm">Click "Generate Payouts" to create records for the selected cycle.</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="annual-bonus-table">
                <thead>
                  <tr className="border-b bg-muted/50 text-[10px] text-muted-foreground uppercase tracking-wider">
                    <th className="py-3 px-4 font-semibold text-center w-10">#</th>
                    <th className="py-3 px-4 font-semibold">Emp ID</th>
                    <th className="py-3 px-4 font-semibold">Employee</th>
                    <th className="py-3 px-4 font-semibold">Organization</th>
                    <th className="py-3 px-4 font-semibold">Joining Date</th>
                    <th className="py-3 px-4 font-semibold text-right">Annual Bonus</th>
                    <th className="py-3 px-4 font-semibold text-center">Months</th>
                    <th className="py-3 px-4 font-semibold text-right">Total Payout</th>
                    <th className="py-3 px-4 font-semibold text-center">Status</th>
                    <th className="py-3 px-4 font-semibold text-center">Finalized</th>
                    <th className="py-3 px-4 font-semibold text-center">Paid</th>
                    <th className="py-3 px-4 font-semibold text-center">Revert</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows}
                  <tr className="bg-muted/60 font-semibold text-sm border-t-2">
                    <td className="py-3 px-4" colSpan={5}>
                      <span className="text-muted-foreground text-xs uppercase tracking-wider">Total</span>
                      <span className="ml-2 text-foreground">{totals.count} employees</span>
                    </td>
                    <td colSpan={2}></td>
                    <td className="py-3 px-4 text-right font-mono font-bold text-emerald-700 text-base">{fmt(totals.total_payout)}</td>
                    <td className="py-3 px-4 text-center text-xs text-muted-foreground">{totals.paid} paid</td>
                    <td className="py-3 px-4 text-center text-xs text-muted-foreground">{totals.finalized}/{totals.count}</td>
                    <td className="py-3 px-4 text-center text-xs text-muted-foreground">{totals.paid}/{totals.count}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Bottom Action Bar */}
        {records.length > 0 && (
          <Card className="bg-muted/20 border-dashed">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  {isAdmin && (
                    <Button size="sm" className="gap-2 bg-blue-600 hover:bg-blue-700 shadow-sm" onClick={handleBulkFinalize} data-testid="bonus-finalize-all-btn">
                      <CheckCheck className="h-3.5 w-3.5" /> Finalize All Pending
                    </Button>
                  )}
                  {isAdmin && (
                    <Button size="sm" variant="outline" className="gap-2" onClick={handleBulkPay} data-testid="bonus-mark-all-paid-btn">
                      <CreditCard className="h-3.5 w-3.5" /> Mark All Paid
                    </Button>
                  )}
                  {isAdmin && (totals.resigned_count || 0) > 0 && (
                    <Button size="sm" variant="outline" className="gap-2 text-amber-700 border-amber-300 hover:bg-amber-50" onClick={handleBulkProrate} data-testid="bonus-bulk-prorate-btn">
                      <UserX className="h-3.5 w-3.5" /> Pro-rate All Resigned ({totals.resigned_count})
                    </Button>
                  )}
                  {isSuperAdmin && (
                    <Button size="sm" variant="outline" className="gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" onClick={handleBulkRevert} data-testid="bonus-revert-all-btn">
                      <RotateCcw className="h-3.5 w-3.5" /> Revert All
                    </Button>
                  )}
                </div>
                <Button size="sm" variant="outline" className="gap-2" onClick={() => exportCSV(records)} data-testid="bonus-export-csv-btn">
                  <Download className="h-3.5 w-3.5" /> Export CSV
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
